import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';
import {
  CreateStaffCommand,
  UpdateStaffCommand
} from '../../../models/Entities/Staff/StaffCommands';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ownFunctions from './Person.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  // entity properties
  const [personType, setPersonType] = useState<PersonTypeEnum>(PersonTypeEnum.NaturalPerson);
  const [businessName, setBusinessName] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  // Fecha y lugar de nacimiento salieron del formulario, pero no de la persona:
  // se devuelven tal como vinieron. Ver `buildPayload`.
  const [activity, setActivity] = useState<string>('');
  const [nationality, setNationality] = useState<string>('');

  const isJuridicalPerson = personType === PersonTypeEnum.JuridicalPerson;

  /**
   * Al abrir para editar se pide la persona.
   *
   * El listado de staff no la trae: de cada miembro publica el id, la razón
   * social y el tipo, y nada de lo que este formulario edita —nombre, apellido,
   * fecha y lugar de nacimiento, actividad, nacionalidad— viaja en él. Sin este
   * pedido el formulario abriría casi vacío y guardar borraría lo que no mostró.
   */
  useEffect(() => {
    if (props.isOpen && props.personId && !props.person) {
      props.GetPersonById(props.personId);
    }
  }, [props.isOpen, props.personId, props.person]);

  useEffect(() => {
    if (props.person) {
      setPersonType(props.person.personType || PersonTypeEnum.NaturalPerson);
      setBusinessName(props.person.businessName || '');
      setFirstName(props.person.firstName || '');
      setLastName(props.person.lastName || '');
      setActivity(props.person.activity || '');
      setNationality(props.person.nationality || '');
    } else {
      resetForm();
    }
  }, [props.person, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setPersonType(PersonTypeEnum.NaturalPerson);
    setBusinessName('');
    setFirstName('');
    setLastName('');
    setActivity('');
    setNationality('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (isJuridicalPerson && !businessName.trim()) {
      errors.push(
        t('persons.form.validation.businessName', { defaultValue: 'La razón social es obligatoria' })
      );
    }

    if (!isJuridicalPerson && !firstName.trim()) {
      errors.push(t('persons.form.validation.firstName', { defaultValue: 'El nombre es obligatorio' }));
    }

    if (!isJuridicalPerson && !lastName.trim()) {
      errors.push(t('persons.form.validation.lastName', { defaultValue: 'El apellido es obligatorio' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const buildPayload = (): CreateStaffCommand => ({
    personType,
    businessName: isJuridicalPerson
      ? businessName.trim()
      : `${firstName.trim()} ${lastName.trim()}`.trim(),
    firstName: isJuridicalPerson ? null : firstName.trim(),
    lastName: isJuridicalPerson ? null : lastName.trim(),
    // Fecha y lugar de nacimiento ya no se editan acá, pero el comando los lleva:
    // se devuelven tal como vinieron. Mandarlos en null sería borrar de la persona
    // un dato que esta pantalla simplemente dejó de mostrar.
    birthDate: props.person?.birthDate ?? null,
    birthPlace: props.person?.birthPlace ?? null,
    activity: activity.trim() || null,
    nationality: nationality.trim() || null,
    // Género, estado civil y cantidad de matrimonios no se editan acá —esta
    // aplicación no los mantiene— pero el comando los lleva, y omitirlos en una
    // modificación los borraría. Se devuelven tal como vinieron.
    genderId: props.person?.gender?.id ?? null,
    maritalStatusId: props.person?.maritalStatus?.id ?? null,
    numberOfMarriages: props.person?.numberOfMarriages ?? 0
  });

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    // Es el id del staff el que decide, no la persona: editando, la persona llega
    // un momento después que el panel se abre, y mirarla haría que ese instante
    // se comportara como un alta.
    if (props.staffId && props.person) {
      const payload: UpdateStaffCommand = {
        ...buildPayload(),
        id: props.staffId,
        personId: props.person.id
      };
      props.UpdateStaff(payload);
    } else {
      props.CreateStaff(buildPayload());
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const selectedPersonTypeOption = CommonFunctions.PersonTypeOptions.find(
    (option) => option.key === personType
  );

  const personTypeText = (option: { textKey: string; defaultText: string }): string =>
    String(t(option.textKey, { defaultValue: option.defaultText }));

  const formFields = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field
        label={t('persons.form.personType', { defaultValue: 'Tipo de persona' })}
        required
      >
        <Dropdown
          value={selectedPersonTypeOption ? personTypeText(selectedPersonTypeOption) : ''}
          selectedOptions={[String(personType)]}
          onOptionSelect={(_, data) => setPersonType(Number(data.optionValue) as PersonTypeEnum)}
        >
          {CommonFunctions.PersonTypeOptions.map((option) => (
            <Option key={option.key} value={String(option.key)} text={personTypeText(option)}>
              {personTypeText(option)}
            </Option>
          ))}
        </Dropdown>
      </Field>

      {isJuridicalPerson && (
        <Field
          label={t('persons.form.businessName', { defaultValue: 'Razón social' })}
          required
          validationMessage={
            submitted && !businessName.trim()
              ? t('persons.form.validation.businessName', { defaultValue: 'La razón social es obligatoria' })
              : undefined
          }
          validationState={submitted && !businessName.trim() ? 'error' : undefined}
        >
          <Input value={businessName} onChange={(_, data) => setBusinessName(data.value || '')} />
        </Field>
      )}

      {!isJuridicalPerson && (
        <>
          <Field
            label={t('persons.form.firstName', { defaultValue: 'Nombre' })}
            required
            validationMessage={
              submitted && !firstName.trim()
                ? t('persons.form.validation.firstName', { defaultValue: 'El nombre es obligatorio' })
                : undefined
            }
            validationState={submitted && !firstName.trim() ? 'error' : undefined}
          >
            <Input value={firstName} onChange={(_, data) => setFirstName(data.value || '')} />
          </Field>

          <Field
            label={t('persons.form.lastName', { defaultValue: 'Apellido' })}
            required
            validationMessage={
              submitted && !lastName.trim()
                ? t('persons.form.validation.lastName', { defaultValue: 'El apellido es obligatorio' })
                : undefined
            }
            validationState={submitted && !lastName.trim() ? 'error' : undefined}
          >
            <Input value={lastName} onChange={(_, data) => setLastName(data.value || '')} />
          </Field>

          <Field label={t('persons.form.nationality', { defaultValue: 'Nacionalidad' })}>
            <Input value={nationality} onChange={(_, data) => setNationality(data.value || '')} />
          </Field>
        </>
      )}

      <Field label={t('persons.form.activity', { defaultValue: 'Actividad' })}>
        <Input value={activity} onChange={(_, data) => setActivity(data.value || '')} />
      </Field>
    </div>
  );

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.person
            ? t('persons.form.editTitle', { defaultValue: 'Editar miembro del staff' })
            : t('persons.form.addTitle', { defaultValue: 'Nuevo miembro del staff' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        {formFields}
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button
            appearance="primary"
            onClick={handleSave}
            disabled={props.isAdding || props.isUpdating}
          >
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonFormComponent as any);
